import React from 'react';
import PropTypes from 'prop-types';
import BeRemoteErrorAlert from 'client/components/Be/BeRemoteErrorAlert';
import BeLoading from 'client/components/Be/BeLoading';
import { ButtonWaiting } from 'client/components/Button/ButtonWaiting';

/**
 * @component EATDTesterPerson
 * @description Renders a single person item for testing EATD rules, including controls and results.
 *              This component expects to be rendered within a list structure managed by a parent.
 */
function EATDTesterPerson ({
  personId,            // The ID of the person being tested
  personName,          // A display name for the person (e.g., dynamicName)
  onApplyChanges,      // Function to call to apply changes: (personId) => void
  onRemove,            // Function to call to remove the person: (personId) => void
  onPreviewChanges,    // Function to call to preview changes: (personId) => void
  isApplying,          // Is the apply action currently running?
  isPreviewing,        // Is the preview action currently running?
  error,               // Any error associated with the last action on this person
  applyResult,         // Result from the last apply action
  previewResult,       // Result from the last preview action
}) {
  /**
   * @function handleApplyChangesClick
   * @description Calls the onApplyChanges prop for this specific person.
   */
  const handleApplyChangesClick = () => {
    onApplyChanges(personId);
  };

  /**
   * @function handlePreviewChangesClick
   * @description Calls the onPreviewChanges prop for this specific person.
   */
  const handlePreviewChangesClick = () => {
    onPreviewChanges(personId);
  };

  /**
   * @function handleRemoveClick
   * @description Calls the onRemove prop for this specific person.
   */
  const handleRemoveClick = () => {
    onRemove(personId);
  };

  const displayName = personName || `Person ID: ${personId}`; // Fallback display name
  const previewData = previewResult?.data || previewResult || null;
  const applyData = applyResult?.data || applyResult || null;
  const applySucceeded = applyData?.status === 'updated' || applyData?.status === 'no_changes';

  return (
    <div className="list-group-item xs-pb-0">
      <h4 className="list-group-item-heading xs-mb-15 xs-mt-0">
        <div className="name pull-left" style={{lineHeight: '30px'}}>
          <i className="icon mdi mdi-account-outline xs-mr-5"></i> {/* Changed Icon */}
          {displayName}
        </div>
        <span className="btn-group pull-right">
          <ButtonWaiting
            type="button"
            className="btn btn-rounded btn-info"
            onClick={handlePreviewChangesClick}
            isWaiting={isPreviewing}
            disabled={isApplying || isPreviewing}
            title="Preview Changes for this Person"
          >
            <i className="icon icon-left mdi mdi-eye-outline"></i>
            Preview
          </ButtonWaiting>
          <ButtonWaiting
            type="button"
            className="btn btn-rounded btn-warning"
            onClick={handleApplyChangesClick}
            isWaiting={isApplying}
            disabled={isApplying || isPreviewing}
            title="Apply EATD Rules to this Person"
          >
            <i className="icon icon-left mdi mdi-pencil-outline"></i>
            Apply Changes
          </ButtonWaiting>
          <button
            type="button"
            className="btn btn-rounded btn-default"
            onClick={handleRemoveClick}
            disabled={isApplying || isPreviewing}
            title="Remove Person from Test List"
          >
            <i className="icon icon-left mdi mdi-delete"></i>
            Remove
          </button>
        </span>
        <div className="clearfix"></div>
      </h4>

      {(isApplying || isPreviewing) && (
        <BeLoading size={20}>
          {isApplying ? `Applying changes to ${displayName}...` : `Previewing changes for ${displayName}...`}
        </BeLoading>
      )}

      {error && <BeRemoteErrorAlert error={error} />}

      {/* Display Preview Result */}
      {previewResult && !error && !isPreviewing && (
        <div className="alert alert-info mt-2 p-2">
          <h6>Preview Results:</h6>
          {previewData?.originalData && (
            <div className="mb-2">
              <div><strong>Original:</strong> {previewData.originalData.dynamicName || previewData.originalData.id || personId}</div>
            </div>
          )}
          {previewData?.proposedChanges && Object.keys(previewData.proposedChanges).length > 0 ? (
            <pre className="bg-light p-2 rounded mb-0 small"><code>{JSON.stringify(previewData.proposedChanges, null, 2)}</code></pre>
          ) : (
            <p className="mb-0">Preview successful, no changes proposed.</p>
          )}
        </div>
      )}

      {/* Display Apply Result */}
      {applyResult && !error && !isApplying && (
        <div className={`alert ${applySucceeded ? 'alert-success' : 'alert-warning'} mt-2 p-2`}>
          <h6>Apply Changes Results:</h6>
          {applyData?.changesApplied && Object.keys(applyData.changesApplied).length > 0 && (
            <pre className="bg-light p-2 rounded mb-0 small"><code>{JSON.stringify(applyData.changesApplied, null, 2)}</code></pre>
          )}
          {applyData?.updatedData && !applyData?.changesApplied && (
            <pre className="bg-light p-2 rounded mb-0 small"><code>{JSON.stringify(applyData.updatedData, null, 2)}</code></pre>
          )}
          {applyData?.status === 'no_changes' && <p className="mb-0">No updates were required for this person.</p>}
          {applyData?.status === 'updated' && !applyData?.changesApplied && !applyData?.updatedData && <p className="mb-0">Apply successful.</p>}
        </div>
      )}
    </div>
  );
}

EATDTesterPerson.propTypes = {
  personId: PropTypes.string.isRequired,
  personName: PropTypes.string, // Optional display name
  onApplyChanges: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onPreviewChanges: PropTypes.func.isRequired,
  isApplying: PropTypes.bool,
  isPreviewing: PropTypes.bool,
  error: PropTypes.object,
  applyResult: PropTypes.any, // Adjust based on actual API response structure
  previewResult: PropTypes.any, // Adjust based on actual API response structure
};

EATDTesterPerson.defaultProps = {
  personName: null,
  isApplying: false,
  isPreviewing: false,
  error: null,
  applyResult: null,
  previewResult: null,
};

export default EATDTesterPerson; 